import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getSuggestions } from "@/utils/search";
import { useClickOutside } from "@/hooks/useClickOutside";

interface SearchBarProps {
  defaultValue?: string;
  size?: "lg" | "md";
  placeholder?: string;
  onSearch?: (keyword: string) => void;
  autoFocus?: boolean;
}

export default function SearchBar({
  defaultValue = "",
  size = "md",
  placeholder = "搜索新闻、视频、百科内容",
  onSearch,
  autoFocus = false,
}: SearchBarProps) {
  const navigate = useNavigate();
  const [value, setValue] = useState(defaultValue);
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  useClickOutside(boxRef, () => setOpen(false));

  const suggestions = getSuggestions(value, 10);

  const submit = (kw: string) => {
    const keyword = kw.trim();
    if (!keyword) return;
    setOpen(false);
    if (onSearch) {
      onSearch(keyword);
    } else {
      navigate(`/search?q=${encodeURIComponent(keyword)}`);
    }
  };

  const isLg = size === "lg";

  return (
    <div
      ref={boxRef}
      className={`relative w-full ${isLg ? "max-w-2xl" : "max-w-xl"}`}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit(value);
        }}
        className={`flex w-full items-center rounded-full border bg-background-50 transition-shadow ${
          open ? "border-primary-400" : "border-background-300"
        } ${isLg ? "h-14 pl-5 pr-2" : "h-11 pl-4 pr-1.5"}`}
      >
        <i className={`ri-search-line text-foreground-500 ${isLg ? "text-lg" : "text-base"}`} />
        <input
          type="text"
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => {
            setValue(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          aria-label="搜索框"
          className={`min-w-0 flex-1 bg-transparent px-3 text-foreground-950 outline-none placeholder:text-foreground-400 ${
            isLg ? "text-base" : "text-sm"
          }`}
        />
        <button
          type="submit"
          className={`flex items-center justify-center rounded-full bg-primary-500 font-medium text-background-50 whitespace-nowrap transition-colors hover:bg-primary-600 ${
            isLg ? "h-11 px-6 text-sm" : "h-8 px-4 text-sm"
          }`}
        >
          搜索
        </button>
      </form>

      {open && suggestions.length > 0 && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-40 overflow-hidden rounded-lg border border-background-200 bg-background-50 py-1">
          <ul>
            {suggestions.map((s) => (
              <li key={s}>
                <button
                  type="button"
                  onMouseDown={() => submit(s)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground-700 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-search-line text-foreground-400" />
                  <span className="flex-1 truncate">{s}</span>
                  <i className="ri-arrow-right-up-line text-foreground-300" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}